import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X, Loader2, ImagePlus, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { AuthImage } from "@/components/common/AuthImage";
import { PhotoMarkupDialog } from "@/components/common/PhotoMarkupDialog";
import { compressMany, fmtKB } from "@/lib/imageCompress";
import { newRowKey } from "@/components/diary/diaryMeta";

/**
 * Captioned photo grid for a diary entry. Every pick is auto-compressed in the browser
 * (≤1200px, ~450 KB target) before it is held as base64; the server re-encodes once more.
 * Existing photos (edit mode) are kept by name; removed ones are deleted on save.
 *
 * `markup` (optional, Phase 90): `{ stampDefaults, title }` turns on a "Mark up" action per photo —
 * circles / arrows / clouds / text / info stamp are flattened into the photo. Existing photos are
 * fetched with auth and replaced by the marked-up version (only the marked-up photo is kept).
 */
export const DiaryPhotosEditor = ({ photos, setPhotos, max = 20, disabled = false, testPrefix = "diary-photos", label = "Site photos", hint, markup = null }) => {
  const fileRef = useRef(null);
  const [progress, setProgress] = useState(null); // {done,total}
  const [markupKey, setMarkupKey] = useState(null);
  const markupTarget = markupKey ? photos.find((p) => p.key === markupKey) : null;

  const applyMarkup = ({ dataUrl, bytes, elements, stamp }) => {
    setPhotos((prev) => prev.map((p) => (p.key === markupKey ? { key: p.key, kind: "new", b64: dataUrl, caption: p.caption || "", bytes, originalBytes: p.originalBytes || p.bytes, markup: { elements, stamp } } : p)));
    toast.success("Markup applied to the photo");
  };

  const onPick = async (e) => {
    const room = max - photos.length;
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (room <= 0) {
      toast.error(`Up to ${max} photos per entry`);
      return;
    }
    const picked = files.slice(0, room);
    if (files.length > room) toast.warning(`Only ${room} more photo${room === 1 ? "" : "s"} fit — the first ${room} were added`);
    setProgress({ done: 0, total: picked.length });
    try {
      const { results, failed } = await compressMany(picked, { onProgress: setProgress });
      const added = results.filter(Boolean).map((r) => ({ key: newRowKey(), kind: "new", b64: r.dataUrl, caption: "", bytes: r.bytes, originalBytes: r.originalBytes }));
      setPhotos((prev) => [...prev, ...added].slice(0, max));
      if (failed.length) toast.error(`${failed.length} file${failed.length === 1 ? " wasn't" : "s weren't"} a readable image`);
      else if (added.length) {
        const before = added.reduce((s, p) => s + (p.originalBytes || 0), 0);
        const after = added.reduce((s, p) => s + (p.bytes || 0), 0);
        if (before > after && before > 0) toast.success(`${added.length} photo${added.length === 1 ? "" : "s"} compressed ${fmtKB(before)} → ${fmtKB(after)}`);
      }
    } catch (err) {
      toast.error(err.message || "Could not process those photos");
    } finally {
      setProgress(null);
    }
  };

  const setCaption = (key, caption) => setPhotos((prev) => prev.map((p) => (p.key === key ? { ...p, caption } : p)));
  const remove = (key) => setPhotos((prev) => prev.filter((p) => p.key !== key));

  return (
    <div className="space-y-2" data-testid={`${testPrefix}-editor`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
          <p className="text-[11px] text-muted-foreground" data-testid={`${testPrefix}-count`}>
            {photos.length} of {max} · {hint || "auto-compressed on upload"}
          </p>
        </div>
        {!disabled && (
          <>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid={`${testPrefix}-input`} />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={photos.length >= max || Boolean(progress)} data-testid={`${testPrefix}-add-btn`}>
              {progress ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Camera className="mr-1.5 h-3.5 w-3.5" />}
              {progress ? `Compressing ${progress.done}/${progress.total}` : "Add photos"}
            </Button>
          </>
        )}
      </div>
      {progress && <Progress value={(progress.done / Math.max(1, progress.total)) * 100} className="h-1.5" data-testid={`${testPrefix}-progress`} />}
      {photos.length === 0 ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => fileRef.current?.click()}
          className="flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-xs text-muted-foreground transition-colors duration-150 hover:bg-muted/40 disabled:cursor-default disabled:hover:bg-muted/20"
          data-testid={`${testPrefix}-empty`}
        >
          <ImagePlus className="h-5 w-5 text-primary" />
          {disabled ? "No photos" : "Tap to add site photos — phone camera or gallery"}
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {photos.map((p, idx) => (
            <div key={p.key} className="rounded-xl border border-border bg-background p-2" data-testid={`${testPrefix}-item-${idx}`}>
              <div className="relative">
                {p.kind === "new" ? (
                  <img src={p.b64} alt={`Photo ${idx + 1}`} className="h-32 w-full rounded-lg border border-border object-cover" data-testid={`${testPrefix}-img-${idx}`} />
                ) : (
                  <AuthImage path={p.path} alt={`Photo ${idx + 1}`} className="h-32 w-full rounded-lg border border-border object-cover" testId={`${testPrefix}-img-${idx}`} />
                )}
                <span className="absolute left-2 top-2 rounded-md bg-background/85 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground backdrop-blur">
                  {idx + 1}
                  {p.bytes ? <span className="ml-1 font-normal text-muted-foreground">· {fmtKB(p.bytes)}</span> : null}
                  {p.markup ? <span className="ml-1 font-normal text-primary" data-testid={`${testPrefix}-marked-${idx}`}>· marked up</span> : null}
                </span>
                {!disabled && markup && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-2 right-2 h-8 px-2.5 text-xs shadow"
                    onClick={() => setMarkupKey(p.key)}
                    aria-label={`Mark up photo ${idx + 1}`}
                    data-testid={`${testPrefix}-markup-${idx}`}
                  >
                    <PenLine className="mr-1 h-3.5 w-3.5" /> Mark up
                  </Button>
                )}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(p.key)}
                    className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow transition-transform duration-150 hover:scale-105"
                    aria-label={`Remove photo ${idx + 1}`}
                    data-testid={`${testPrefix}-remove-${idx}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Input
                value={p.caption}
                onChange={(e) => setCaption(p.key, e.target.value)}
                placeholder="Caption — what does this show?"
                className="mt-2 h-8 text-xs"
                disabled={disabled}
                maxLength={300}
                data-testid={`${testPrefix}-caption-${idx}`}
              />
            </div>
          ))}
        </div>
      )}
      {markup && (
        <PhotoMarkupDialog
          open={Boolean(markupTarget)}
          onOpenChange={(v) => !v && setMarkupKey(null)}
          src={markupTarget?.kind === "new" ? markupTarget.b64 : undefined}
          authPath={markupTarget?.kind === "existing" ? markupTarget.path : undefined}
          title={markup.title || "Mark up photo"}
          subtitle={markupTarget ? `Photo ${photos.findIndex((p) => p.key === markupTarget.key) + 1}` : ""}
          stampDefaults={markup.stampDefaults}
          initial={markupTarget?.markup}
          onSave={applyMarkup}
        />
      )}
    </div>
  );
};
